import { supabase } from "@/integrations/supabase/client";
import { format, parse, isWithinInterval, areIntervalsOverlapping } from "date-fns";
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { BUSINESS_TIMEZONE, MINIMUM_BOOKING_LEAD_TIME_MINUTES } from './timezoneConfig';

export interface TimeSlot {
  startTime: Date;
  endTime: Date;
  isAvailable: boolean;
  isAfterHours: boolean;
  requiresApproval: boolean;
  reason?: string;
}

export interface AvailabilityResult {
  available: boolean;
  reason?: string;
  isAfterHours: boolean;
}

/**
 * Gets all available time slots for a specific date
 */
export async function getAvailableSlots(
  date: Date,
  adminId?: string
): Promise<TimeSlot[]> {
  try {
    const dayOfWeek = date.getDay();

    // Fetch admin availability for this day of week
    let query = supabase
      .from('admin_availability')
      .select('*')
      .eq('day_of_week', dayOfWeek)
      .eq('is_available', true);

    // Filter by admin_id if available
    if (adminId) {
      query = query.eq('admin_id', adminId);
    }

    const { data: availability, error: availError } = await query
      .limit(1)
      .maybeSingle();

    if (availError) throw availError;
    if (!availability) return []; // No availability set for this day

    // Parse start and end times
    const startTime = parse(availability.start_time, 'HH:mm:ss', date);
    const endTime = parse(availability.end_time, 'HH:mm:ss', date);
    const slotDuration = availability.slot_duration_minutes || 60;

    // Determine effective end time (includes after-hours if configured)
    let effectiveEndTime = endTime;
    if (availability.allow_after_hours && availability.after_hours_end) {
      const afterHoursEndTime = parse(availability.after_hours_end, 'HH:mm:ss', date);
      // Use the later of the two times
      effectiveEndTime = afterHoursEndTime > endTime ? afterHoursEndTime : endTime;
    }

    // Fetch blocked time slots for this date
    const dateStr = format(date, 'yyyy-MM-dd');
    const { data: blockedSlots, error: blockedError } = await supabase
      .from('blocked_time_slots')
      .select('*')
      .eq('blocked_date', dateStr);

    if (blockedError) throw blockedError;

    // Fetch booked time slots for this date using the database function
    const { data: existingAppointments, error: aptError } = await supabase
      .rpc('get_booked_time_slots', { check_date: dateStr });

    if (aptError) throw aptError;

    // Generate time slots
    const slots: TimeSlot[] = [];
    let currentSlotStart = new Date(startTime);
    
    // Get current time in Central Time zone
    const nowCT = toZonedTime(new Date(), BUSINESS_TIMEZONE);
    const dateCT = toZonedTime(date, BUSINESS_TIMEZONE);
    const isToday = format(dateCT, 'yyyy-MM-dd') === format(nowCT, 'yyyy-MM-dd');

    while (currentSlotStart < effectiveEndTime) {
      const currentSlotEnd = new Date(currentSlotStart.getTime() + slotDuration * 60000);
      
      if (currentSlotEnd > effectiveEndTime) break;

      // Skip slots that have already passed or don't have enough lead time (only for today)
      // Convert slot start time to Central Time for comparison
      const currentSlotStartCT = toZonedTime(currentSlotStart, BUSINESS_TIMEZONE);
      const minimumBookingTime = new Date(nowCT.getTime() + MINIMUM_BOOKING_LEAD_TIME_MINUTES * 60000);

      if (isToday && currentSlotStartCT <= minimumBookingTime) {
        currentSlotStart = new Date(currentSlotEnd);
        continue;
      }

      // Check if slot is after hours
      let isAfterHours = false;
      if (availability.allow_after_hours && availability.after_hours_start && availability.after_hours_end) {
        const afterHoursStart = parse(availability.after_hours_start, 'HH:mm:ss', date);
        const afterHoursEnd = parse(availability.after_hours_end, 'HH:mm:ss', date);
        isAfterHours = isWithinInterval(currentSlotStart, { start: afterHoursStart, end: afterHoursEnd });
      }

      // Check if slot is blocked
      const isBlocked = blockedSlots?.some(blocked => {
        if (blocked.is_all_day) return true;
        if (!blocked.start_time || !blocked.end_time) return false;
        
        const blockedStart = parse(blocked.start_time, 'HH:mm:ss', date);
        const blockedEnd = parse(blocked.end_time, 'HH:mm:ss', date);
        
        return areIntervalsOverlapping(
          { start: currentSlotStart, end: currentSlotEnd },
          { start: blockedStart, end: blockedEnd }
        );
      });

      // Check if slot conflicts with existing appointments
      const hasConflict = existingAppointments?.some(apt => {
        if (!apt.start_time || !apt.end_time) return false;
        
        const aptStart = new Date(apt.start_time);
        const aptEnd = new Date(apt.end_time);
        
        return areIntervalsOverlapping(
          { start: currentSlotStart, end: currentSlotEnd },
          { start: aptStart, end: aptEnd }
        );
      });

      let reason: string | undefined;
      let isAvailable = true;

      if (isBlocked) {
        isAvailable = false;
        reason = 'Time slot is blocked';
      } else if (hasConflict) {
        isAvailable = false;
        reason = 'Time slot is already booked';
      } else if (isAfterHours) {
        isAvailable = true;
        reason = 'After-hours appointment (requires approval)';
      }

      slots.push({
        startTime: new Date(currentSlotStart),
        endTime: new Date(currentSlotEnd),
        isAvailable,
        isAfterHours,
        requiresApproval: isAfterHours,
        reason,
      });

      currentSlotStart = new Date(currentSlotEnd);
    }

    return slots;
  } catch (error) {
    console.error('Error fetching available slots:', error);
    return [];
  }
}

/**
 * Checks if a specific time slot is available
 */
export async function isTimeSlotAvailable(
  startTime: Date,
  endTime: Date,
  adminId?: string
): Promise<AvailabilityResult> {
  try {
    const dayOfWeek = startTime.getDay();
    const dateStr = format(startTime, 'yyyy-MM-dd');

    // Check admin availability
    let query = supabase
      .from('admin_availability')
      .select('*')
      .eq('day_of_week', dayOfWeek)
      .eq('is_available', true);

    if (adminId) {
      query = query.eq('admin_id', adminId);
    }

    const { data: availability, error: availError } = await query
      .limit(1)
      .maybeSingle();

    if (availError) throw availError;
    if (!availability) {
      return { available: false, reason: 'Admin not available on this day', isAfterHours: false };
    }

    // Parse availability hours
    const availStartTime = parse(availability.start_time, 'HH:mm:ss', startTime);
    const availEndTime = parse(availability.end_time, 'HH:mm:ss', startTime);

    // Check if time falls within regular hours
    const isWithinRegularHours = 
      startTime >= availStartTime && 
      endTime <= availEndTime;

    // Check if after hours
    let isAfterHours = false;
    if (availability.allow_after_hours && availability.after_hours_start && availability.after_hours_end) {
      const afterHoursStart = parse(availability.after_hours_start, 'HH:mm:ss', startTime);
      const afterHoursEnd = parse(availability.after_hours_end, 'HH:mm:ss', startTime);
      isAfterHours = isWithinInterval(startTime, { start: afterHoursStart, end: afterHoursEnd });
    }

    if (!isWithinRegularHours && !isAfterHours) {
      return { available: false, reason: 'Time outside available hours', isAfterHours: false };
    }

    // Check blocked time slots
    const { data: blockedSlots, error: blockedError } = await supabase
      .from('blocked_time_slots')
      .select('*')
      .eq('blocked_date', dateStr);

    if (blockedError) throw blockedError;

    const isBlocked = blockedSlots?.some(blocked => {
      if (blocked.is_all_day) return true;
      if (!blocked.start_time || !blocked.end_time) return false;
      
      const blockedStart = parse(blocked.start_time, 'HH:mm:ss', startTime);
      const blockedEnd = parse(blocked.end_time, 'HH:mm:ss', startTime);
      
      return areIntervalsOverlapping(
        { start: startTime, end: endTime },
        { start: blockedStart, end: blockedEnd }
      );
    });

    if (isBlocked) {
      return { available: false, reason: 'Time slot is blocked', isAfterHours };
    }

    // Check existing appointments using the database function
    const { data: existingAppointments, error: aptError } = await supabase
      .rpc('get_booked_time_slots', { check_date: dateStr });

    if (aptError) throw aptError;

    const hasConflict = existingAppointments?.some(apt => {
      if (!apt.start_time || !apt.end_time) return false;
      
      const aptStart = new Date(apt.start_time);
      const aptEnd = new Date(apt.end_time);
      
      return areIntervalsOverlapping(
        { start: startTime, end: endTime },
        { start: aptStart, end: aptEnd }
      );
    });

    if (hasConflict) {
      return { available: false, reason: 'Time slot already booked', isAfterHours };
    }

    return { 
      available: true, 
      isAfterHours,
      reason: isAfterHours ? 'After-hours appointment (requires approval)' : undefined
    };
  } catch (error) {
    console.error('Error checking time slot availability:', error);
    return { available: false, reason: 'Error checking availability', isAfterHours: false };
  }
}

/**
 * Gets the configured slot duration for a specific date
 */
export async function getSlotDuration(date: Date, adminId?: string): Promise<number> {
  try {
    const dayOfWeek = date.getDay();

    let query = supabase
      .from('admin_availability')
      .select('slot_duration_minutes')
      .eq('day_of_week', dayOfWeek)
      .eq('is_available', true);

    if (adminId) {
      query = query.eq('admin_id', adminId);
    }

    const { data: availability, error } = await query
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    return availability?.slot_duration_minutes || 60;
  } catch (error) {
    console.error('Error fetching slot duration:', error);
    return 60; // Default to 60 minutes
  }
}

/**
 * Checks if a time is in after-hours range
 */
export async function isAfterHoursTime(time: Date, adminId?: string): Promise<boolean> {
  try {
    const dayOfWeek = time.getDay();

    let query = supabase
      .from('admin_availability')
      .select('allow_after_hours, after_hours_start, after_hours_end')
      .eq('day_of_week', dayOfWeek)
      .eq('is_available', true);

    if (adminId) {
      query = query.eq('admin_id', adminId);
    }

    const { data: availability, error } = await query
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    if (!availability?.allow_after_hours || !availability.after_hours_start || !availability.after_hours_end) {
      return false;
    }

    const afterHoursStart = parse(availability.after_hours_start, 'HH:mm:ss', time);
    const afterHoursEnd = parse(availability.after_hours_end, 'HH:mm:ss', time);
    
    return isWithinInterval(time, { start: afterHoursStart, end: afterHoursEnd });
  } catch (error) {
    console.error('Error checking after hours:', error);
    return false;
  }
}
